import { apiClient } from "./apiClient";
import { FornoItem } from "./fornoService";
import { AguardandoEntregaItem } from "./aguardandoEntregaService";
import type { FilaDoceItem, FilaSalgadaItem } from "./filaPizzasService";

export interface TemposMediosPainel {
  aguardandoMontagemSegundos: number | null;
  emMontagemSegundos: number | null;
  emFornoSegundos: number | null;
  aguardandoEntregaSegundos: number | null;
  emEntregaSegundosPorEndereco: number | null;
}

export interface MontagemPainel {
  startedAt: string;
  finishedAt: string | null;
  tempoTotal: number | null;
}

export type PainelFilaTipo =
  | "doce"
  | "salgada"
  | "salao_doce"
  | "salao_salgada";

export interface PainelPedidoFoodflyResumo {
  identificador: string | null;
  mesaId?: string | null;
  comandaId?: string | null;
  numeroPedido: number;
  observacao: string | null;
}

export type PainelFilaItem = Omit<
  FilaDoceItem | FilaSalgadaItem,
  "tipo" | "pedidoIntegracaoId"
> & {
  tipo: PainelFilaTipo;
  /** `cardapio` = Cardápio Web; `salao` = FoodFly (mesa/salão). Ausente = cardápio. */
  origem?: "cardapio" | "salao";
  pedidoIntegracaoId?: number | null;
  /** Presente quando `origem === "salao"`. */
  pedidoFoodflyId?: number | null;
  pedidoFoodfly?: PainelPedidoFoodflyResumo | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  tempoTotal?: number | null;
  montagens?: MontagemPainel[];
};

export type PainelFinalizadoOrigem = "forno_retirada" | "entrega" | "balcao";

export interface PainelFinalizadoTempos {
  aguardandoMontagemSeg: number | null;
  emMontagemSeg: number | null;
  emFornoSeg: number | null;
  aguardandoEntregaSeg: number | null;
  emEntregaSeg: number | null;
}

export interface PainelFinalizadoItem {
  key: string;
  displayId: number;
  orderId: number;
  descricao: string | null;
  orderType: string | null;
  customerName: string | null;
  /** Nome do pizzaiolo que montou. */
  montadorNome?: string | null;
  finalizadoEm: string;
  origem: PainelFinalizadoOrigem;
  /** Canal de venda: Cardápio Web vs FoodFly (salão). Ausente = cardápio. */
  canalOrigem?: "cardapio" | "salao";
  /** Identificador da mesa no FoodFly (ex.: "Mesa 04"). */
  salaoIdentificador?: string | null;
  /** ID da mesa no FoodFly (ex.: "8"). */
  salaoMesaId?: string | null;
  /** ID da comanda no FoodFly (quando não é pedido de mesa). */
  salaoComandaId?: string | null;
  tempos: PainelFinalizadoTempos;
}

export interface PainelFinalizadosPaginatedResponse {
  data: PainelFinalizadoItem[][];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  dataInicio: string;
  dataFim: string;
}

export interface PainelPaozinhoAguardandoBordeiro {
  id: number;
  displayId: number;
  orderId: number;
  saborNome: string;
  saborChave: string;
  createdAt: string;
  origem?: "cardapio" | "salao";
  salaoMesaId?: string | null;
  salaoComandaId?: string | null;
  salaoIdentificador?: string | null;
}

export interface PainelAguardandoBordaItem {
  id: number;
  tipo: PainelFilaTipo;
  displayId: number;
  orderId: number;
  bordaNome: string;
  customerName: string | null;
  descricao: unknown;
  paradoDesde: string;
}

/** Takeout pronto no balcão (`/painel/takeouts-prontos`): data alinhada ao `finished_at` do forno no dia. */
export interface PainelTakeoutProntoItem {
  displayId: number;
  orderId: number;
  customerName: string | null;
  prontoEm: string;
}

export type OperacaoDiaAlertaNivel = "ok" | "atencao" | "alerta" | "critico";

export type OperacaoDiaEtapa =
  | "integracao_pendente"
  | "integracao_erro"
  | "sem_fila"
  | "cancelado"
  | "aguardando_pizzaiolo"
  | "aguardando_borda"
  | "em_montagem"
  | "no_forno"
  | "aguardando_entrega"
  | "em_entrega"
  | "finalizado";

export interface OperacaoDiaPedidoRow {
  pedidoIntegracaoId: number;
  pedidoFoodflyId?: number | null;
  canalOrigem?: "cardapio" | "salao";
  orderId: number;
  displayId: number;
  customerName: string | null;
  salaoIdentificador?: string | null;
  salaoMesaId?: string | null;
  salaoComandaId?: string | null;
  orderType: string | null;
  chegouEm: string;
  etapa: OperacaoDiaEtapa;
  etapaLabel: string;
  paradoDesde: string | null;
  paradoSegundos: number | null;
  alertaNivel: OperacaoDiaAlertaNivel;
  motivoParado: string | null;
  resumoItens: string | null;
  isPrioritario: boolean;
  pizzasNaFila: number;
  pizzasTotal: number;
  montadores: string | null;
}

export interface OperacaoDiaSequenciaPaginatedResponse {
  data: OperacaoDiaPedidoRow[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const SEQUENCIA_DIA_PAGE_SIZE = 30;

export interface PainelData {
  aguardandoMontagem: PainelFilaItem[];
  emMontagem: PainelFilaItem[];
  emForno: FornoItem[];
  aguardandoEntrega: AguardandoEntregaItem[];
  emEntrega: AguardandoEntregaItem[];
  /** Retirada no balcão: `order_type` takeout + produção encerrada (ver backend). */
  takeoutsProntos: PainelTakeoutProntoItem[];
  finalizados: PainelFinalizadoItem[];
  temposMedios: TemposMediosPainel;
  paozinhosAguardandoBordeiro: PainelPaozinhoAguardandoBordeiro[];
  aguardandoBorda: PainelAguardandoBordaItem[];
}

function formatTempoMedio(segundos: number): string {
  const s = Math.max(0, Math.round(segundos));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export function somaSegundosTemposMediosEtapas(
  t: TemposMediosPainel
): number | null {
  const partes: number[] = [];
  if (t.aguardandoMontagemSegundos != null) {
    partes.push(t.aguardandoMontagemSegundos);
  }
  if (t.emMontagemSegundos != null) partes.push(t.emMontagemSegundos);
  if (t.emFornoSegundos != null) partes.push(t.emFornoSegundos);
  if (t.aguardandoEntregaSegundos != null) {
    partes.push(t.aguardandoEntregaSegundos);
  }
  if (t.emEntregaSegundosPorEndereco != null) {
    partes.push(t.emEntregaSegundosPorEndereco);
  }
  if (partes.length === 0) return null;
  return partes.reduce((a, b) => a + b, 0);
}

export const painelService = {
  async getTemposMedios(lojaId?: number): Promise<TemposMediosPainel> {
    const params = new URLSearchParams();
    if (lojaId != null) params.set("lojaId", String(lojaId));
    const qs = params.toString();
    return apiClient.get<TemposMediosPainel>(
      `/painel/tempos-medios${qs ? `?${qs}` : ""}`
    );
  },

  formatTempoMedio,
  somaSegundosTemposMediosEtapas,

  async getPainel(lojaId?: number): Promise<PainelData> {
    const params = new URLSearchParams();
    if (lojaId != null) params.set("lojaId", String(lojaId));
    const qs = params.toString();
    return apiClient.get<PainelData>(`/painel/agregado${qs ? `?${qs}` : ""}`);
  },

  async getFinalizados(params: {
    lojaId?: number;
    page: number;
    limit: number;
    dataInicio: string;
    dataFim: string;
    q?: string;
    canalOrigem?: "cardapio" | "salao";
  }): Promise<PainelFinalizadosPaginatedResponse> {
    const search = new URLSearchParams();
    if (params.lojaId != null) search.set("lojaId", String(params.lojaId));
    search.set("page", String(params.page));
    search.set("limit", String(params.limit));
    search.set("dataInicio", params.dataInicio);
    search.set("dataFim", params.dataFim);
    const q = params.q?.trim();
    if (q) search.set("q", q);
    if (params.canalOrigem) search.set("canalOrigem", params.canalOrigem);
    return apiClient.get<PainelFinalizadosPaginatedResponse>(
      `/painel/finalizados?${search.toString()}`,
    );
  },

  formatSegundosParaBadge(segundos: number): string {
    const m = Math.floor(segundos / 60);
    const s = Math.round(segundos % 60);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  },

  async getSequenciaDia(params?: {
    lojaId?: number;
    page?: number;
    limit?: number;
    displayId?: number;
    canalOrigem?: "cardapio" | "salao";
  }): Promise<OperacaoDiaSequenciaPaginatedResponse> {
    const search = new URLSearchParams();
    if (params?.lojaId != null) search.set("lojaId", String(params.lojaId));
    if (params?.page != null) search.set("page", String(params.page));
    if (params?.limit != null) search.set("limit", String(params.limit));
    if (params?.displayId != null) {
      search.set("displayId", String(params.displayId));
    }
    if (params?.canalOrigem != null) {
      search.set("canalOrigem", params.canalOrigem);
    }
    const qs = search.toString();
    return apiClient.get<OperacaoDiaSequenciaPaginatedResponse>(
      `/painel/sequencia-dia${qs ? `?${qs}` : ""}`
    );
  },
};
